/**
 * CREATE-WORK-ORDER.JSX - Work Order Creation/Editing Container Component
 *
 * SUMMARY:
 * Refactored container component for creating and editing Work Orders (WO) in the contractor module.
 * This component orchestrates multiple custom hooks and presentational components to provide
 * a clean, maintainable architecture following separation of concerns principles.

 */

import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Button from 'react-bootstrap/Button';
import idx from "idx";
import '../scss/task.scss';
import { resolveProjectType } from "../utils/resolve-project-type";

// Constants
import {
  PROJECT_MANAGEMENT,
  CONTRACTORS,
  WORK_ORDER,
  VENDOR,
  VENDOR_ADD_CONTRACTOR
} from "../../../constants/route-paths";

// Actions
import { errorSnackBar } from "../../../actions/common.action";
import { editPurchaseOrder, savePurchaseOrder } from "../../../actions/project-management.action";

// Custom Hooks
import { useWorkOrderForm } from "./hooks/useWorkOrderForm";
import { useWorkOrderFileUpload } from "./hooks/useWorkOrderFileUpload";
import { useWorkOrderTaskSelection } from "./hooks/useWorkOrderTaskSelection";
import { useWorkOrderData } from "./hooks/useWorkOrderData";
import { useManageItemsModal } from "./hooks/useManageItemsModal";

// Components
import WorkOrderBreadcrumb from "./components/WorkOrderBreadcrumb";
import WorkOrderFormFields from "./components/WorkOrderFormFields";
import WorkOrderTaskSelector from "./components/WorkOrderTaskSelector";
import WorkOrderItemsTable from "./components/WorkOrderItemsTable";
import ManageItemsModal from "../../../components/manage-items-modal";

// Utils
import { TaskType } from "../../../util/enums";
import { findOptionInTree } from "./utils/work-order-helpers";

/**
 * Generates a unique ID for a work order line item.
 * Used to ensure each WO line can be uniquely identified when receiving invoices.
 *
 * @returns {string} Unique line item ID in format 'wo-item-{timestamp}-{random}'
 */
const generateWoItemId = () => {
  return `wo-item-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
};

/**
 * Main Work Order creation/editing container component.
 *
 * @component
 * @example
 * // Create new WO in project context
 * <Route path="/projects/work-order/:projectId/create" element={<CreateWorkOrder />} />
 *
 * // Edit existing WO
 * <Route path="/projects/work-order/:projectId/edit/:poId" element={<CreateWorkOrder />} />
 */
export default function CreateWorkOrder(props) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId, poId } = useParams();
  const [searchParams] = useSearchParams();

  // Refs
  const poDataRef = useRef(null);

  const navStateProjectName = location.state?.projectName || "";
  const navStateProjectType = location.state?.projectType || "";
  const fromProjectWOList = location.state?.from === 'project-wo-list';
  const editWoProjectId = location.state?.projectId;
  const projectState = useSelector(state => state.project);

  // Local state initialized with nav state if available
  const [woId, setwoId] = useState();
  const [projectType, setProjectType] = useState(navStateProjectType);
  const [projectName, setProjectName] = useState(navStateProjectName);

  // Initialize state with nav state if available (fallback for updates)
  useEffect(() => {
    if (navStateProjectName && !projectName) setProjectName(navStateProjectName);
    if (navStateProjectType && !projectType) setProjectType(navStateProjectType);
  }, [navStateProjectName, navStateProjectType]);

  // Custom hooks
  const {
    fields,
    setFields,
    inputChangeHandler,
    vendorChangeHandler,
    changeHandler,
    validateForm
  } = useWorkOrderForm([], projectId);

  const {
    fileToUpload,
    removedFiles,
    handleFileUpload,
    handleRemoveFile,
    handleRemoveExistingFile,
    setFileToUpload
  } = useWorkOrderFileUpload(poDataRef);

  const {
    allTasks,
    attributionDropdownOptions,
    taskByIdRef,
    originalSectionData,
    loadProjectTasks,
    fetchWorkOrderData,
    initializeVendors,
    initializeProjects
  } = useWorkOrderData({ setFields, setProjectType, setProjectName });

  const {
    selectedTasks,
    selectedItems,
    handleTaskChange,
    handleAttributionChange,
    handleDescriptionChange,
    handleCostChange,
    handleRemoveLineItem,
    handleAddManualItem,
    validateLineItems,
    setSelectedTasks,
    setSelectedItems
  } = useWorkOrderTaskSelection({
    taskByIdRef,
    allTasks,
    originalSectionData
  });

  const {
    modalOpen,
    availableItems,
    checkedItemsSet,
    openModal,
    closeModal,
    toggleItem,
    applySelection
  } = useManageItemsModal({
    selectedTasks,
    selectedItems,
    taskByIdRef,
    setSelectedItems
  });

  /**
   * Initialize component data on mount.
   * Loads vendors, projects (if not in project context), and existing WO data (if editing).
   *
   * LIFECYCLE: ComponentDidMount equivalent
   */
  useEffect(() => {
    const init = async () => {
      const { allTasks: loadedAllTasks, attributionOptions: loadedAttributionOptions } = await initializeData();
      if (poId) fetchWOData(loadedAllTasks, loadedAttributionOptions);
    };
    init();
  }, [projectId, poId]);

  /**
   * Initializes required data for the component.
   *
   * @private
   */
  const initializeData = async () => {
    let allTasks = [];
    let attributionOptions = [];

    const tasksPromise = !projectId
      ? initializeProjects().catch(() => dispatch(errorSnackBar('Failed to load project list')))
      : loadProjectTasks(projectId);

    const vendorPromise = !fields.vendor.items.length
      ? initializeVendors().catch(() => dispatch(errorSnackBar('Failed to load vendor list')))
      : Promise.resolve();

    const [tasksResult] = await Promise.all([tasksPromise, vendorPromise]);
    if (tasksResult) {
      allTasks = tasksResult.allTasks || [];
      attributionOptions = tasksResult.attributionOptions || [];
    }

    return { allTasks, attributionOptions };
  };

  /**
   * Fetches existing work order data for editing.
   *
   * @private
   */
  const fetchWOData = async (loadedAllTasks = allTasks, loadedAttributionOptions = attributionDropdownOptions) => {
    const poData = await fetchWorkOrderData(poId, projectId, projectChangeHandler);

    if (!poData) return;

    setwoId(poData.id);
    setFileToUpload([]);

    // Build selectedTasks and selectedItems from loaded data
    if (Array.isArray(poData.items)) {
      const loadedTasks = poData.items.map(item => ({
        taskId: item.taskId,
        taskName: item.taskName,
        cost: item.total || 0,
        amount: item.total || 0,
        taskType: item.taskType || TaskType.TASK
      }));

      const loadedItems = poData.items.map((item, index) => {
        const taskOption = findOptionInTree(loadedAttributionOptions, item.taskId);
        return {
          isManual: true,
          manualId: `loaded-${item.taskId}-${index}-${Date.now()}`,
          id: item.id || generateWoItemId(), // Backfill ID for existing WOs
          taskId: item.taskId,
          taskName: item.taskName,
          taskType: item.taskType || TaskType.TASK,
          subTaskId: null,
          subTaskName: '',
          subcontractorItemId: null,
          description: item.name || item.taskName || '',
          cost: item.total || 0,
          amount: item.total || 0,
          // Restore original task info for modal grouping
          originalTaskId: item.originalTaskId,
          originalTaskName: item.originalTaskName,
          // Restore full stage path for hierarchical display (backend may not return it)
          stagePath: item.stagePath || taskOption?.stagePath
        };
      });

      setSelectedTasks(loadedTasks);
      setSelectedItems(loadedItems);
    }

    poDataRef.current = poData;
  };

  /**
   * Handles project selection change.
   * Clears existing task/item selections and loads new project's tasks.
   *
   * @param {string} value - Selected project ID
   * @param {Array<string>} alwaysIncludeTaskIds - Task IDs to always include
   */
  const projectChangeHandler = async (value, alwaysIncludeTaskIds = []) => {
    setSelectedTasks([]);
    setSelectedItems([]);
    await loadProjectTasks(value, alwaysIncludeTaskIds);
  };

  /**
   * Navigates to vendor edit page.
   * Preserves state to return to WO creation after editing.
   *
   * @param {string} id - Vendor ID to edit
   */
  const editVendor = (id) => {
    localStorage.setItem('createWOState', 'true');
    const fromVendorOrders = location.pathname.includes('/vendor/orders');

    if (fromVendorOrders) {
      const returnParams = new URLSearchParams(location.search);
      if (!returnParams.get('returnTab')) {
        returnParams.set('returnTab', 'work-orders');
      }
      returnParams.set('returnTo', 'create-wo');
      navigate(`/${VENDOR}/${VENDOR_ADD_CONTRACTOR}/edit/${id}?${returnParams.toString()}`);
    } else {
      navigate(`/contractors/edit/${id}`);
    }
  };

  /**
   * Submits the work order form.
   * Validates form fields and line items, builds FormData, and dispatches save/edit action.
   *
   * @async
   */
  const submitForm = async () => {
    const poData = poDataRef.current;
    const rfqId = idx(poData, _ => _.rfqId) || '';

    // Validate form fields
    if (!validateForm()) {
      return;
    }

    // Validate that all line items have a task/section selected
    if (!validateLineItems()) {
      dispatch(errorSnackBar('Please select a task/section for each line.'));
      return;
    }

    // Build FormData
    const data = new FormData();

    data.set('projectId', projectId || fields.project.value);
    data.set('vendorId', fields.vendor.value);
    data.set('quoteNo', fields.quote_no.value);
    data.set('street_address', fields.street_address.value);
    data.set('suburb', fields.suburb.value);
    data.set('state', fields.state.value);
    data.set('postcode', fields.postcode.value);
    data.set('country', fields.country.value);

    // Add PO-level fallback attribution
    if (selectedItems.length > 0) {
      const firstItemWithTask = selectedItems.find(it => it.taskId) || selectedItems[0];
      data.set('taskId', firstItemWithTask.taskId);
      data.set('taskType', firstItemWithTask.taskType);
    }

    // Add line items
    selectedItems.forEach(item => {
      const isStageAttribution = item.taskType === TaskType.STAGE;

      data.append("items[]", JSON.stringify({
        id: item.id || generateWoItemId(), // Stable unique ID for invoice matching
        taskId: item.taskId,
        taskName: item.taskName,
        total: parseFloat(item.amount ?? 0),
        contractCost: parseFloat(item.cost ?? 0),
        taskType: item.taskType,
        name: item.description,
        isStage: isStageAttribution,
        stageId: isStageAttribution ? item.taskId : null,
        // Preserve original task info for modal grouping (when different from attribution)
        originalTaskId: item.originalTaskId,
        originalTaskName: item.originalTaskName,
        // Preserve full stage path for hierarchical display
        stagePath: item.stagePath
      }));
    });

    // Add file attachments
    for (let i = 0; i < fileToUpload.length; i++) {
      data.append("attachment", fileToUpload[i]);
    }

    if (poData?.quotationId) {
      data.set("quotationId", poData.quotationId);
    }

    // Add removed files
    removedFiles.forEach(key => {
      data.append('removedFiles[]', key);
    });

    const successMsg = poId ? 'Work Order Updated Successfully' : 'Work Order Created Successfully';

    // Submit
    if (poId) {
      await dispatch(editPurchaseOrder(data, poId, successMsg))
        .catch(e => dispatch(errorSnackBar('Failed to update Work Order')));
    } else {
      await dispatch(savePurchaseOrder(data, rfqId, successMsg))
        .catch(e => dispatch(errorSnackBar('Failed to create Work Order')));
    }

    viewWoList();
  };

  // Navigation helpers
  const resolvedProjectId = projectId || editWoProjectId || fields.project.value;

  const viewWoList = () => {
    if (fromProjectWOList && resolvedProjectId) {
      navigate(`/projects/work-order/${resolvedProjectId}`);
    } else if (resolvedProjectId) {
      navigate(`/${PROJECT_MANAGEMENT}/work-order/${resolvedProjectId}`);
    } else {
      viewContractorWoList();
    }
  };

  const viewProject = () => {
    if (resolvedProjectId) {
      navigate(`/projects/view/${resolvedProjectId}`);
    } else {
      dispatch(errorSnackBar('Project ID not found'));
    }
  };
  const viewProjectList = () => {
    let type = resolveProjectType(searchParams.get("project-type") || projectType, projectState);
    navigate(`/projects?project-type=${type}`);
  }

  const viewContractorWoList = () => {
    if (props.customNavigate) {
      props.customNavigate(`/${CONTRACTORS}/${WORK_ORDER}`);
    } else {
      navigate(`/${CONTRACTORS}/${WORK_ORDER}`);
    }
  };

  return (
    <>
      <div className="purchase-order-block" data-cy="create-wo">
        <h2 className="content-heading text-uppercase" data-cy="heading">
          {poId ? 'Edit WO - ' + woId : 'Create Work Order'}
        </h2>

        <WorkOrderBreadcrumb
          projectType={projectType}
          projectName={projectName}
          poId={poId}
          viewProjectList={viewProjectList}
          viewProject={viewProject}
          viewWoList={viewWoList}
        />

        <WorkOrderFormFields
          fields={fields}
          projectId={projectId}
          projectChangeHandler={projectChangeHandler}
          inputChangeHandler={inputChangeHandler}
          vendorChangeHandler={vendorChangeHandler}
          changeHandler={changeHandler}
          handleFileUpload={handleFileUpload}
          handleRemoveFile={handleRemoveFile}
          handleRemoveExistingFile={handleRemoveExistingFile}
          editVendor={editVendor}
          fileToUpload={fileToUpload}
          poDataRef={poDataRef}
        />

        <WorkOrderTaskSelector
          selectedTasks={selectedTasks}
          allTasks={allTasks}
          handleTaskChange={handleTaskChange}
          handleOpenModal={openModal}
        />

        <WorkOrderItemsTable
          selectedItems={selectedItems}
          attributionDropdownOptions={attributionDropdownOptions}
          handleAttributionChange={handleAttributionChange}
          handleDescriptionChange={handleDescriptionChange}
          handleCostChange={handleCostChange}
          handleRemoveLineItem={handleRemoveLineItem}
          handleAddManualItem={handleAddManualItem}
        />

        <div className="col-12 d-flex mt-3">
          <Button
            type="submit"
            className="ms-auto me-3 secondarybtn"
            data-cy="submit-button"
            onClick={submitForm}
          >
            DONE
          </Button>
          <Button
            type="submit"
            className="secondarybtn cancelbtn me-auto"
            data-cy="cancel-button"
            onClick={viewWoList}
          >
            CANCEL
          </Button>
        </div>
      </div>

      <ManageItemsModal
        open={modalOpen}
        onClose={closeModal}
        availableItems={availableItems}
        checkedItemsSet={checkedItemsSet}
        onToggleItem={toggleItem}
        onApply={applySelection}
      />
    </>
  );
}
